import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Brain, Save, PlusCircle, Tag, RefreshCw, Code, Users, Building2, Wrench, Globe, GraduationCap, Book, Briefcase } from 'lucide-react';
import { skillsService } from '../services/api';
import { Alert, AlertDescription } from '../components/ui/alert';

const CATEGORY_ICONS = {
  skills: <Wrench className="h-5 w-5" />,
  assets: <Briefcase className="h-5 w-5" />,
  relationships: <Users className="h-5 w-5" />,
  areas_of_knowledge: <Book className="h-5 w-5" />,
  certifications: <Tag className="h-5 w-5" />,
  degrees: <GraduationCap className="h-5 w-5" />
};

const CATEGORY_NAMES = {
  skills: "Skills",
  assets: "Assets",
  relationships: "Relationships",
  areas_of_knowledge: "Areas of Knowledge",
  certifications: "Certifications",
  degrees: "Degrees"
};

const MySkills = () => {
  // Local state for selected skills and changes
  const [selectedSkills, setSelectedSkills] = useState({
    skills: {},
    assets: {},
    relationships: {},
    areas_of_knowledge: {},
    certifications: {},
    degrees: {}
  });
  const [originalSkills, setOriginalSkills] = useState({});
  const [availableSkills, setAvailableSkills] = useState({});
  const [newCustomSkills, setNewCustomSkills] = useState([]);
  const [newSkill, setNewSkill] = useState('');
  const [selectedCategoryType, setSelectedCategoryType] = useState('skills');
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchUserSkills();
    fetchAvailableSkills();
  }, []);

  
  const fetchUserSkills = async () => {
    try {
      console.log('Starting to fetch user skills...');
      setIsLoading(true);
      
      // Fetch skills from API
      const userSkills = await skillsService.getUserSkills();
      console.log('Raw API response:', userSkills);
      
      // Check if skills exist in the response
      if (userSkills && userSkills.skills) {
        // Map the skills to the correct category
        const mappedSkills = {
          skills: userSkills.skills || {},
          assets: userSkills.assets || {},
          relationships: userSkills.relationships || {},
          areas_of_knowledge: userSkills.areas_of_knowledge || {},
          certifications: userSkills.certifications || {},
          degrees: userSkills.degrees || {}
        };
        setSelectedSkills(mappedSkills);
        setOriginalSkills(JSON.parse(JSON.stringify(mappedSkills)));
        console.log('Skills loaded successfully:', mappedSkills);
      } else {
        // Initialize with empty structure if no skills found
        const emptySkills = {
          skills: {},
          assets: {},
          relationships: {},
          areas_of_knowledge: {},
          certifications: {},
          degrees: {}
        };
        setSelectedSkills(emptySkills);
        setOriginalSkills(JSON.parse(JSON.stringify(emptySkills)));
        console.log('No skills found, initialized empty structure');
      }
    } catch (error) {
      console.error('Error fetching skills:', error);
      setSaveStatus({
        type: 'error',
        message: 'Failed to load your skills. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };


  const fetchAvailableSkills = async () => {
    try {
      console.log('fetchAvailableSkills:');
      const skillsList = await skillsService.getSkillsList();
      setAvailableSkills(skillsList.categories || {});
    } catch (error) {
      console.error('Error fetching available skills:', error);
      setSaveStatus({
        type: 'error',
        message: 'Failed to load available skills. Please refresh the page.'
      });
    }
  };

  const handleSkillToggle = (categoryType, category, skill) => {
    console.log(`Toggling skill: ${skill} in ${category} (${categoryType})`);
    setSelectedSkills(prev => {
       // Create a deep copy of the previous state
      const updatedSkills = JSON.parse(JSON.stringify(prev));

      //ensure proper structure exists
      if (!updatedSkills[categoryType]) {
        updatedSkills[categoryType] = {};
      }
      if (!updatedSkills[categoryType][category]) {
        updatedSkills[categoryType][category] = [];
      }

      // Get current skills array
      const currentSkills = updatedSkills[categoryType][category];
      const skillIndex = currentSkills.indexOf(skill);

      //Toggle skill
      if (skillIndex === -1) {
        currentSkills.push(skill);
      } else {
        currentSkills.splice(skillIndex, 1);
      }

      console.log(`Updated ${categoryType}.${category}:`, currentSkills);
      return updatedSkills;
    });
  };

  const handleAddCustomSkill = (categoryType, category) => {
    console.log('handleAddCustomSkill:');
    if (!newSkill.trim()) return;

    const customSkill = newSkill.trim();
    
    // Add to selected skills
    handleSkillToggle(categoryType, category, customSkill);
    
    // Track new custom skill for backend update
    console.log('setNewCustomSkills:');
    setNewCustomSkills(prev => [...prev, {
      categoryType,
      category,
      skill: customSkill
    }]);

    setNewSkill('');
  };
  
  // Fix the isSkillSelected function to properly check skill selection
const isSkillSelected = (categoryType, category, skill) => {
  return selectedSkills[categoryType]?.[category]?.includes(skill) || false;
};
  
  const handleSave = async () => {
    console.log('handleSave:');
    console.log('selectedSkills:',selectedSkills);
    console.log('newCustomSkills:',newCustomSkills);
    try {
      setIsLoading(true);
      setSaveStatus({ type: '', message: '' });
      
      await skillsService.saveSkills(selectedSkills, newCustomSkills);
      
      // Update original skills after successful save
      setOriginalSkills(JSON.parse(JSON.stringify(selectedSkills)));
      setNewCustomSkills([]);
      
      setSaveStatus({
        type: 'success',
        message: 'Your skills have been saved successfully.'
      });
    } catch (error) {
      console.error('Error saving skills:', error);
      setSaveStatus({
        type: 'error',
        message: 'Failed to save your skills. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const hasUnsavedChanges = () => {
    return JSON.stringify(selectedSkills) !== JSON.stringify(originalSkills) || newCustomSkills.length > 0;
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Card className="w-full mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-6 w-6" />
            My Skills Profile
          </CardTitle>
          <div className="mt-4 text-gray-600">
            <p className="mb-4">
              Build your comprehensive profile by selecting your skills, assets, relationships, 
              and knowledge areas. This information helps us understand your capabilities and 
              connect you with relevant opportunities.
            </p>
          </div>
        </CardHeader>
        <CardContent>
          {/* Status Alert */}
          {saveStatus.message && (
            <Alert className={`mb-6 ${saveStatus.type === 'error' ? 'bg-red-50' : 'bg-green-50'}`}>
              <AlertDescription>
                {saveStatus.message}
              </AlertDescription>
            </Alert>
          )}

        {/* Skills Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {Object.entries(selectedSkills).map(([categoryType, categories]) => 
            Object.entries(categories).map(([category, skills]) => 
              Array.isArray(skills) && skills.length > 0 && (
                <Card key={`${categoryType}-${category}`} className="bg-gray-50">
                  <CardHeader className="p-4">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {CATEGORY_ICONS[categoryType]}
                      {category}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="flex flex-wrap gap-2">
                      {skills.map(skill => (
                        <span 
                          key={skill} 
                          className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )
            )
          )}
        </div>


          {/* Category Type Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {Object.entries(CATEGORY_NAMES).map(([type, name]) => (
              <button
                key={type}
                onClick={() => setSelectedCategoryType(type)}
                className={`px-4 py-2 rounded-md transition-colors ${
                  selectedCategoryType === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  {CATEGORY_ICONS[type]}
                  {name}
                </div>
              </button>
            ))}
          </div>

          {/* Skills Selection Section */}
          <div className="space-y-6">
            {availableSkills[selectedCategoryType] && 
              Object.entries(availableSkills[selectedCategoryType]).map(([category, skills]) => (
                <Card key={category} className="w-full">
                  <CardHeader className="p-4">
                    <CardTitle className="text-lg">
                      {category}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {skills.map(skill => {
                        const isSelected = selectedSkills[selectedCategoryType]?.[category]?.includes(skill) ?? false;
                        return (
                          <button
                            key={skill}
                            onClick={() => handleSkillToggle(selectedCategoryType, category, skill)}
                            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                              isSelected
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                            }`}
                          >
                            {skill}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Add custom skill..."
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                        className="flex-1 p-2 border rounded"
                      />
                      <button
                        onClick={() => handleAddCustomSkill(selectedCategoryType, category)}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
                      >
                        <PlusCircle className="h-4 w-4" />
                        Add Custom
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>


          <div className="mt-6 flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {hasUnsavedChanges() && 'You have unsaved changes'}
            </div>
            <div className="flex gap-4">
              <button
                onClick={fetchUserSkills}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
                disabled={isLoading}
              >
                <RefreshCw className="h-4 w-4" />
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={!hasUnsavedChanges() || isLoading}
                className={`px-4 py-2 rounded-md flex items-center gap-2
                  ${hasUnsavedChanges() && !isLoading
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                <Save className="h-4 w-4" />
                Save Changes
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MySkills;